import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useFetcher, useLocation } from 'react-router-dom';
import { Box, Checkbox, FormControlLabel } from '@mui/material';
import { FeedbackPopup, FeedbackType } from 'src/components/feedback_popup';
import { Warning } from 'src/components/icons/warning';
import {
  SquareCheckedIcon,
  SquareUncheckedIcon,
} from 'src/components/multi_select_checkbox_group';
import { SectionHeader } from 'src/components/section_header';
import { useWorkflowType } from 'src/distribution_platforms/features/hooks/use_workflow';
import { useIsLdtCustomerSuccess } from 'src/hooks/use_auth';
import { LdtOrderDetailFieldsFragment, OrderStatus } from 'src/types';
import { DistributionPlatform } from 'src/types/env';
import { OrderAction, OrderActionResult } from 'src/types/loaders';
import { WorkflowType } from 'src/types/workflows';
import { notifyError, notifySuccess } from 'src/utils';

const BILLING_HOLD_EXCLUDED_STATUSES: OrderStatus[] = [
  OrderStatus.ReportReady,
  OrderStatus.ReportFaxPending,
  OrderStatus.ReportFaxed,
  OrderStatus.ReportFaxFailed,
];

export type BillingHoldSectionOrder = Pick<
  LdtOrderDetailFieldsFragment,
  'id' | 'status' | 'hasBillingHold'
>;

export const BillingHoldSection: FC<{
  order: BillingHoldSectionOrder;
  platform: DistributionPlatform;
}> = ({ order, platform }) => {
  const fetcher = useFetcher();
  const location = useLocation();
  const workflowType = useWorkflowType();
  const isCustomerSuccessUser = useIsLdtCustomerSuccess();
  const [addBillingHoldConfirmOpen, setAddBillingHoldConfirmOpen] =
    useState(false);
  const lastBillingHoldResultRef = useRef<unknown>(null);

  const showBillingHoldSection =
    platform === DistributionPlatform.Ldt &&
    isCustomerSuccessUser &&
    workflowType === WorkflowType.CLINIC &&
    order.status != null &&
    !BILLING_HOLD_EXCLUDED_STATUSES.includes(order.status);

  const handleBillingHoldChange = useCallback(() => {
    const newValue = !order.hasBillingHold;
    if (newValue) {
      setAddBillingHoldConfirmOpen(true);
    } else {
      const formData = new FormData();
      formData.append('action', OrderAction.UPDATE_BILLING_HOLD);
      formData.append('hasBillingHold', 'false');
      fetcher.submit(formData, { method: 'post', action: location.pathname });
    }
  }, [order.hasBillingHold, fetcher, location.pathname]);

  const handleConfirmAddBillingHold = useCallback(() => {
    setAddBillingHoldConfirmOpen(false);
    const formData = new FormData();
    formData.append('action', OrderAction.UPDATE_BILLING_HOLD);
    formData.append('hasBillingHold', 'true');
    fetcher.submit(formData, { method: 'post', action: location.pathname });
  }, [fetcher, location.pathname]);

  const handleCancelAddBillingHold = useCallback(() => {
    setAddBillingHoldConfirmOpen(false);
  }, []);

  useEffect(() => {
    if (
      fetcher.state !== 'idle' ||
      !fetcher.data ||
      lastBillingHoldResultRef.current === fetcher.data
    )
      return;
    const result = fetcher.data as OrderActionResult;
    if (result.action !== OrderAction.UPDATE_BILLING_HOLD) return;
    lastBillingHoldResultRef.current = fetcher.data;
    if (result.success && result.order) {
      notifySuccess('Billing hold updated successfully.');
    } else {
      notifyError(
        result.error ?? 'Failed to update billing hold. Please try again.',
      );
    }
  }, [fetcher.state, fetcher.data, order.id]);

  if (!showBillingHoldSection) {
    return null;
  }

  return (
    <>
      <Box>
        <FormControlLabel
          control={
            <Checkbox
              checked={order.hasBillingHold ?? false}
              color='primary'
              data-testid='billing-hold-checkbox'
              disabled={fetcher.state === 'submitting'}
              icon={<SquareUncheckedIcon />}
              checkedIcon={<SquareCheckedIcon />}
              onChange={handleBillingHoldChange}
            />
          }
          label={<SectionHeader title='Billing Hold' />}
        />
      </Box>
      <FeedbackPopup
        isDialogOpen={addBillingHoldConfirmOpen}
        handleDialogClose={handleCancelAddBillingHold}
        onPrimaryButtonClick={handleConfirmAddBillingHold}
        onSecondaryButtonClick={handleCancelAddBillingHold}
        data={{
          title: 'Add billing hold?',
          primaryButtonText: 'YES, I CONFIRM',
          secondaryButtonText: 'NO',
          icon: Warning,
          type: FeedbackType.Primary,
        }}
        minHeight='240px'
      />
    </>
  );
};
